import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import DownloadAsset from '../../property-assets-management/download-assets/DownloadAsset';
import { useGetPhotosetsQuery } from '../../property-assets-management/services/assetApi';
import { isInternalAssetTeamUser } from '../../property-assets-management/assetFeatureFlags';
import { useOrgWatermarkAvailable } from '@/hooks/useOrgWatermarkAvailable';
import { orderPhotosetDownload } from './model/orderPhotosetDownload';

interface OrderDownloadAssetProps {
  orderUUID: string;
  propertyUUID: string | undefined;
  /** The row's `resolvedPhotosetUUID` (DV-3366): the order's photoset. */
  photosetUUID: string | null | undefined;
  zipNameBase: string;
  setShowModel: (show: boolean) => void;
}

/**
 * DV-2763: the orders row's download — the order's own photoset, with the
 * "Include hidden photos?" opt-in the Manage Assets page offers for it. The
 * photos come from the same gallery read that page makes (scoped to the
 * session's account, and without any set hidden from this customer), and go
 * to the download as explicit photoUUIDs, so a customer's download can never
 * carry a hidden staged photo (asset-service's order-tagged download with
 * includeHidden would).
 *
 * The read is always made fresh: none of the workspace's edits (hide,
 * reorder, caption sort, visibility, staging commits) invalidate the shared
 * cache entry, so a cached list can still show a photo as visible that the
 * user hid a moment ago. A failed read is an error with Retry, never a quiet
 * fall back to the order-tagged download this replaced.
 *
 * With no photoset answer (see orderPhotosetDownload) it keeps the order-tagged
 * download the row always made. Mounts with the modal, so the read and the
 * session's watermark answer are never made for a row nobody opened.
 */
export const OrderDownloadAsset = ({
  orderUUID,
  propertyUUID,
  photosetUUID,
  zipNameBase,
  setShowModel
}: OrderDownloadAssetProps) => {
  const watermarkAvailable = useOrgWatermarkAvailable();
  const readsPhotoset = Boolean(photosetUUID && propertyUUID);
  const { currentData, isFetching, isError, startedTimeStamp, refetch } =
    useGetPhotosetsQuery(propertyUUID ?? '', {
      skip: !readsPhotoset,
      refetchOnMountOrArgChange: true
    });
  // The hook's first render still reports the cached entry as settled; the
  // refetch starts in an effect after it. Only a read started since the modal
  // opened counts, so a stale list never reaches the download options.
  const [openedAt] = useState(Date.now);
  const readSinceOpen = (startedTimeStamp ?? 0) >= openedAt;

  if (readsPhotoset && (!readSinceOpen || isFetching)) {
    return (
      <div
        role="status"
        aria-label="Loading photos"
        className="flex justify-center py-16"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (readsPhotoset && isError) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Couldn't Load Photos"
        description="We couldn't load this order's photos to download. Try again."
        action={<Button onClick={() => refetch()}>Retry</Button>}
        className="h-auto py-12"
      />
    );
  }

  const target = orderPhotosetDownload(
    currentData ?? [],
    photosetUUID,
    isInternalAssetTeamUser()
  );

  return (
    <DownloadAsset
      setShowModel={setShowModel}
      selection={target?.selection ?? { orderUUID }}
      hiddenPhotoUUIDs={target?.hiddenPhotoUUIDs ?? []}
      zipNameBase={zipNameBase}
      watermarkAvailable={watermarkAvailable}
    />
  );
};
